import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  List, ListItem, Checkbox, Box, Divider, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ProofreadIssue } from '../types/proofread';

interface ProofreadReviewDialogProps {
  open: boolean;
  issues: ProofreadIssue[];
  contentLabel?: string;
  // "Send" for emails and announcements; editors that save rather than send pass "Save".
  actionVerb?: string;
  onCancel: () => void;
  onSendAnyway: () => void;
  onApplyAndSend: (selectedIssues: ProofreadIssue[]) => void;
}

// Generic accept/reject review UI for any Grok proofread result - reusable as-is by any feature
// that calls the /api/content/proofread endpoint (email today, announcements later).
const ProofreadReviewDialog: React.FC<ProofreadReviewDialogProps> = ({
  open, issues, contentLabel = 'content', actionVerb = 'Send', onCancel, onSendAnyway, onApplyAndSend
}) => {
  const [checked, setChecked] = useState<boolean[]>([]);

  useEffect(() => {
    if (open) {
      setChecked(issues.map(() => true));
    }
  }, [open, issues]);

  const toggle = (index: number) => {
    setChecked(prev => prev.map((c, i) => (i === index ? !c : c)));
  };

  const selectedCount = checked.filter(Boolean).length;

  const handleApplyAndSend = () => {
    const selected = issues.filter((_, i) => checked[i]);
    onApplyAndSend(selected);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          We found a few possible issues
          <IconButton onClick={onCancel} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Grok checked this {contentLabel} for grammar and spelling issues first. Review the suggestions below,
          uncheck anything you'd rather keep as-is, then {actionVerb.toLowerCase()}.
        </Typography>
        <List dense disablePadding>
          {issues.map((issue, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider component="li" />}
              <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                <Checkbox
                  edge="start"
                  checked={!!checked[index]}
                  onChange={() => toggle(index)}
                  sx={{ mt: -0.5 }}
                />
                <Box>
                  <Typography variant="body2">
                    <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                      {issue.originalText}
                    </Typography>
                    {' → '}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: 'success.main', fontStyle: issue.suggestedText ? 'normal' : 'italic' }}>
                      {issue.suggestedText || '(remove)'}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {issue.reason}
                  </Typography>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSendAnyway} color="inherit">{actionVerb} As Written</Button>
        <Button onClick={handleApplyAndSend} variant="contained" color="success" disabled={selectedCount === 0}>
          Fix Selected & {actionVerb}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProofreadReviewDialog;
